import React, { useState } from "react";

import useEsTelefono from "../../../../utils/esTelefono";
import HttpManager from "../../../../services/HttpManager";
import { EventDetailDTO, ExtraSaleDTO } from "../../../../models/EventDTO";
import S from "./strings";
import { Modal, dinero, dineroCod, errorText } from "../workshops/comun";
import { cuando } from "./cuando";

interface Props { campusId: number; evento: EventDetailDTO; soloLectura: boolean; reload: () => void; notify: (t: string, ok?: boolean) => void; }

/** Vender un extra del evento a un asistente: el cobro va a la alumna (o a la graduada que invita). */
const VenderModal: React.FC<{ campusId: number; evento: EventDetailDTO; onClose: () => void; onDone: () => void }> = ({ campusId, evento, onClose, onDone }) => {
	const http = HttpManager.getInstance();
	// Graduadas y participantes primero: son las que compran extras casi siempre
	const vigentes = evento.attendees.filter(a => a.status === 1)
		.sort((a, b) => Number(b.kind === "graduada" || b.kind === "participante") - Number(a.kind === "graduada" || a.kind === "participante") || a.name.localeCompare(b.name));
	const [para, setPara] = useState<number>(0);
	const [key, setKey] = useState<number>(evento.extras.length === 1 ? evento.extras[0].key || 0 : 0);
	const [busy, setBusy] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const guardar = () => {
		setBusy(true); setError(null);
		http.eventsService.addExtra(campusId, para, key).then(onDone).catch(e => { setError(errorText(e)); setBusy(false); });
	};
	return (
		<Modal title={S.venderExtra} onClose={onClose} ancho={460}>
			<label className="wau-campo" style={{ marginBottom: 10 }}><span className="wau-lab">{S.extraDe}</span>
				<select className="wau-entrada" value={para} onChange={e => setPara(Number(e.target.value))}>
					<option value={0}>{S.elegir}</option>
					{vigentes.map(a => <option key={a.id} value={a.id}>{a.name} · {a.kindLabel}</option>)}
				</select></label>
			<label className="wau-campo" style={{ marginBottom: 10 }}><span className="wau-lab">{S.extraCual}</span>
				<select className="wau-entrada" value={key} onChange={e => setKey(Number(e.target.value))}>
					<option value={0}>{S.elegir}</option>
					{evento.extras.map(x => <option key={x.key} value={x.key}>{x.concept} · {dineroCod(evento.currency, x.price)}</option>)}
				</select></label>
			{error ? <p className="wau-adv">{error}</p> : null}
			<div className="wau-acciones">
				<button type="button" className="wau-btn s" onClick={onClose} disabled={busy}>{S.volver}</button>
				<button type="button" className="wau-btn p" onClick={guardar} disabled={busy || !para || !key}>{S.agregarBtn}</button>
			</div>
		</Modal>
	);
};

/** Extras vendidos: quién, qué, si ya pagó y si ya se entregó (para que no se olvide hacerlo). */
const ExtrasTab: React.FC<Props> = ({ campusId, evento, soloLectura, reload, notify }) => {
	const http = HttpManager.getInstance();
	const movil = useEsTelefono();
	const [vender, setVender] = useState<boolean>(false);
	const [busy, setBusy] = useState<boolean>(false);
	const ventas = evento.extraSales || [];
	const porEntregar = ventas.filter(x => !x.deliveredAt).length;

	const accion = (p: Promise<any>, texto?: string) => {
		setBusy(true);
		p.then(() => { if (texto) notify(texto); reload(); }).catch(e => notify(errorText(e), false)).finally(() => setBusy(false));
	};
	const pago = (x: ExtraSaleDTO) => <><div className="wau-num">{dinero(evento.currency, x.charge ? x.charge.total : x.price)}</div>
		<div className={x.paid ? "wau-sec" : "wau-oro"} style={{ fontSize: 12.5 }}>{x.paid ? S.pagado : S.porPagar + (x.charge ? " " + dinero(evento.currency, x.charge.pending) : "")}</div></>;
	const entregado = (x: ExtraSaleDTO) => (
		<label style={{ display: "inline-flex", gap: 6, alignItems: "center", margin: 0, position: "static" }}>
			<input type="checkbox" checked={!!x.deliveredAt} disabled={busy || soloLectura} onChange={e => accion(http.eventsService.deliverExtra(campusId, x.id, e.target.checked))} />
			<span className={x.deliveredAt ? "wau-sec" : "wau-oro"} style={{ fontSize: 13 }}>{x.deliveredAt ? cuando(x.deliveredAt) + (x.deliveredBy ? " · " + x.deliveredBy : "") : S.porEntregar}</span>
		</label>
	);
	const quitar = (x: ExtraSaleDTO) => soloLectura || x.paid ? null
		: <button type="button" className="wau-btn s mini" aria-label={S.quitar} disabled={busy} onClick={() => accion(http.eventsService.removeExtra(campusId, x.id), S.extraQuitado)}>×</button>;
	const quien = (x: ExtraSaleDTO) => <><b>{x.name}</b><div className={x.attendeeActive ? "wau-sec" : "wau-oro"} style={{ fontSize: 12.5 }}>{x.attendeeActive ? x.kindLabel : S.deBaja}</div></>;

	return (
		<div className="wau-caja">
			<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
				<h3 style={{ margin: 0 }}>{S.tabs.extras} {porEntregar ? <span className="wau-oro wau-num" style={{ fontSize: 14 }}>({S.nPorEntregar(porEntregar)})</span> : null}</h3>
				{soloLectura || !evento.extras.length ? null : <button type="button" className="wau-btn p" onClick={() => setVender(true)}>{S.venderExtra}</button>}
			</div>
			{ventas.length === 0 ? <p className="wau-sec" style={{ margin: 0 }}>{S.sinExtras}</p> : movil ? (
				<div>{ventas.map(x => (
					<div key={x.id} className="wau-tarjeta">
						<div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}><span>{quien(x)}</span>{quitar(x)}</div>
						<div className="linea">{x.concept}</div>
						<div style={{ marginTop: 6 }}>{pago(x)}</div>
						<div style={{ marginTop: 6 }}>{entregado(x)}</div>
					</div>
				))}</div>
			) : (
				<div className="wau-tabla">
					<table>
						<thead><tr><th>{S.colNombre}</th><th>{S.colExtra}</th><th className="der">{S.colPago}</th><th>{S.colEntregado}</th><th></th></tr></thead>
						<tbody>{ventas.map(x => (
							<tr key={x.id}>
								<td>{quien(x)}</td>
								<td>{x.concept}</td>
								<td className="der">{pago(x)}</td>
								<td>{entregado(x)}</td>
								<td className="der">{quitar(x)}</td>
							</tr>
						))}</tbody>
					</table>
				</div>
			)}
			{vender ? <VenderModal campusId={campusId} evento={evento} onClose={() => setVender(false)} onDone={() => { setVender(false); notify(S.extraOk); reload(); }} /> : null}
		</div>
	);
};

export default ExtrasTab;
